import React, { useCallback, useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import { depthToColor } from '../../utils/layer_style';
import { scaleTime } from '@visx/scale';
import MiniDepthChart from './MiniDepthChart';
import type { GeoJsonFeature } from '../../utils/ogc';
import type { MaxPrecipDataFeatureProperties } from '../../layers/max_precip_data';

type BrushedProps = {
  timeRange: [Date, Date, boolean];
  currentTime?: Date;
  playing?: boolean;
  precip?: MaxPrecipDataFeatureProperties[];
  features: GeoJsonFeature[];
  loading?: boolean;
  highlightedKey?: string | null;
  onClick?: (feature: GeoJsonFeature) => void;
  onRangeChange?: (from: Date, to: Date) => void;
  onTimeRangeChange?: (newRange: [Date, Date]) => void;
  height?: number;
  caption?: string;
};

const moveRangeWithCurrentTime = (from: Date, to: Date, currentTime: Date, fullFrom: Date, fullTo: Date) => {
  const w = to.getTime() - from.getTime();
  const f = from.getTime();
  const t = to.getTime();
  const c = currentTime.getTime();
  const ff = fullFrom.getTime();
  const ft = fullTo.getTime();
  const nf = Math.max(ff, Math.min(c - w / 2, ft - w));
  const nt = Math.min(ft, Math.max(c + w / 2, ff + w));
  // preserve original Date identity when computed times didn't change
  // console.log(w, nf-f, nt-t, nf===f, nt===t, { nf, f, nt, t });
  return [
    nf,
    nt
  ];
}

export default function BrushedMiniDepthChart({
  timeRange,
  currentTime,
  playing,
  precip,
  loading,
  features,
  highlightedKey,
  onClick,
  onRangeChange,
  onTimeRangeChange,
  height = 120,
  caption,
  ...props
}: BrushedProps) {
  const [fromAll, toAll] = timeRange;
  const [fromSel_, setFromSel] = React.useState<Date>(fromAll);
  const [toSel_, setToSel] = React.useState<Date>(toAll);
  const [fromSel_ms, toSel_ms] = useMemo(() => 
    playing && currentTime ? moveRangeWithCurrentTime(fromSel_, toSel_, currentTime, fromAll, toAll) : [fromSel_, toSel_], 
  [playing, currentTime, fromAll, toAll, fromSel_, toSel_]);
  const fromSel = useMemo(() => new Date(fromSel_ms), [fromSel_ms]);
  const toSel = useMemo(() => new Date(toSel_ms), [toSel_ms]);

  React.useEffect(() => {
    // setFromSel(f => (f > fromAll && f < toAll ? f : fromAll));
    // setToSel(t => (t > fromAll && t < toAll ? t : toAll));
    setFromSel(fromAll);
    setToSel(toAll);
  }, [fromAll, toAll]);

  // React.useEffect(() => {
  //   if (playing || !currentTime) return;
  //   // const [nf, nt] = moveRangeWithCurrentTime(fromSel, toSel, currentTime!, fromAll, toAll);
  //   setFromSel(f => );
  //   setToSel(toSel);
  // }, [playing, currentTime, fromAll, toAll]);
  const dirty = useMemo(
    () => fromSel.getTime() !== fromAll.getTime() || toSel.getTime() !== toAll.getTime(),
    [fromSel, toSel, fromAll, toAll]
  );

  const commitSelection = useCallback(() => {
    const a = fromSel < toSel ? fromSel : toSel;
    const b = fromSel < toSel ? toSel : fromSel;
    onRangeChange?.(a, b);
    onTimeRangeChange?.([a, b]);
  }, [fromSel, toSel, onRangeChange, onTimeRangeChange]);

  const resetSelection = useCallback(() => {
    setFromSel(fromAll);
    setToSel(toAll);
  }, [fromAll, toAll]);

  // Ensure any applied range stays within bounds and above the minimum zoom span
  const MIN_ZOOM_MS = 5 * 60 * 1000; // avoid zooming into an unusably tiny range
  const applyRange = useCallback((nfMs: number, ntMs: number) => {
    let nf = Math.min(nfMs, ntMs);
    let nt = Math.max(nfMs, ntMs);
    const fullStart = fromAll.getTime();
    const fullEnd = toAll.getTime();
    if (nt - nf < MIN_ZOOM_MS) {
      const mid = (nf + nt) / 2;
      nf = mid - MIN_ZOOM_MS / 2;
      nt = mid + MIN_ZOOM_MS / 2;
    }
    if (nf < fullStart) {
      const delta = fullStart - nf;
      nf = fullStart;
      nt = Math.min(fullEnd, nt + delta);
    }
    if (nt > fullEnd) {
      const delta = nt - fullEnd;
      nt = fullEnd;
      nf = Math.max(fullStart, nf - delta);
    }
    if (nt - nf < MIN_ZOOM_MS) {
      nt = Math.min(fullEnd, nf + MIN_ZOOM_MS);
      nf = Math.max(fullStart, nt - MIN_ZOOM_MS);
    }
    setFromSel(new Date(nf));
    setToSel(new Date(nt));
  }, [fromAll, toAll, MIN_ZOOM_MS]);

  // layout
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [w, setW] = React.useState(600);
  React.useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setW(Math.max(240, Math.floor(e.contentRect.width)));
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  const hMini = 30;
  const pad = 0;
  // Full-range scale (minimap)
  const xScaleAll = React.useMemo(() => scaleTime<number>({ domain: [fromAll, toAll], range: [pad, w - pad] }), [fromAll, toAll, w]);
  // Selected-range scale (main chart overlay)
  const xScaleSel = React.useMemo(() => scaleTime<number>({ domain: [fromSel, toSel], range: [pad, w - pad] }), [fromSel, toSel, w]);
  const miniEventBands = React.useMemo(() => {
    const out: Array<{ x: number; width: number; color: string; height: number; y: number }> = [];
    let maxDepth = 0;
    for (const f of features || []) {
      const events = (f?.properties?.past_events as GeoJsonFeature[]) || [];
      for (const e of events) {
        const ps = e.properties?.start_time;
        const pe = e.properties?.end_time;
        if (!(ps instanceof Date) || !(pe instanceof Date) || isNaN(+ps) || isNaN(+pe)) continue;
        const s = new Date(Math.max(+fromAll, +ps));
        const t = new Date(Math.min(+toAll, +pe));
        if (+t <= +s) continue;

        const d = Number(e.properties?.max_depth_inches) || 0;
        if (d > maxDepth) maxDepth = d;
      }
    }
    const depthScaleMax = Math.max(maxDepth, 1);
    for (const f of features || []) {
      const events = (f?.properties?.past_events as GeoJsonFeature[]) || [];
      for (const e of events) {
        const ps = e.properties?.start_time;
        const pe = e.properties?.end_time;
        if (!(ps instanceof Date) || !(pe instanceof Date) || isNaN(+ps) || isNaN(+pe)) continue;
        const s = new Date(Math.max(+fromAll, +ps));
        const t = new Date(Math.min(+toAll, +pe));
        if (+t <= +s) continue;
        const depth = Number(e.properties?.max_depth_inches) || 0;
        const x1 = xScaleAll(s);
        const x2 = xScaleAll(t);
        const width = Math.max(1, Math.abs(x2 - x1));
        const [r, g, b] = depthToColor(depth);
        const color = `rgba(${r}, ${g}, ${b}, 0.28)`;
        const barHeight = Math.max(3, ((depth / depthScaleMax) || 0) * (hMini - 6));
        const y = hMini - 2 - barHeight;
        out.push({ x: Math.min(x1, x2), width, color, height: barHeight, y });
      }
    }
    return out.sort((a, b) => b.width - a.width);
  }, [features, fromAll, toAll, xScaleAll, hMini]);

  // Drag-to-pan on main chart (replaces in-chart brushing)
  const suppressClickRef = React.useRef<boolean>(false);
  const panRef = React.useRef<{ startX: number; left: number; width: number; fromMs: number; toMs: number } | null>(null);
  const clamp = useCallback((v: number, min: number, max: number) => Math.max(min, Math.min(max, v)), []);
  const [miniPreview, setMiniPreview] = React.useState<[Date, Date] | null>(null);
  const onOverlayDownCapture = useCallback((e: React.MouseEvent<SVGRectElement>) => {
    const bounds = e.currentTarget.getBoundingClientRect();
    const startX = e.clientX - bounds.left;
    panRef.current = {
      startX,
      left: bounds.left,
      width: bounds.width || w,
      fromMs: fromSel.getTime(),
      toMs: toSel.getTime(),
    };
    suppressClickRef.current = false;
    const onMove = (ev: MouseEvent) => {
      if (!panRef.current) return;
      const x = ev.clientX - panRef.current.left;
      const dx = x - panRef.current.startX;
      const spanMs = panRef.current.toMs - panRef.current.fromMs;
      const msPerPx = spanMs / Math.max(1, panRef.current.width);
      const dt = -dx * msPerPx;
      let nf = panRef.current.fromMs + dt;
      let nt = panRef.current.toMs + dt;
      const span = nt - nf;
      if (nf < fromAll.getTime()) { nf = fromAll.getTime(); nt = nf + span; }
      if (nt > toAll.getTime()) { nt = toAll.getTime(); nf = nt - span; }
      applyRange(nf, nt);
      if (Math.abs(dx) > 3) suppressClickRef.current = true;
    };
    const onUp = () => {
      if (!panRef.current) return;
      const spanMs = panRef.current.toMs - panRef.current.fromMs;
      const msPerPx = spanMs / Math.max(1, panRef.current.width);
      const currentFrom = fromSel.getTime();
      const currentTo = toSel.getTime();
      const movedPx = (panRef.current.fromMs - currentFrom) / msPerPx;
      if (Math.abs(movedPx) > 2) suppressClickRef.current = true;
      panRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('mouseleave', onUp as any);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('mouseleave', onUp as any);
  }, [fromSel, toSel, fromAll, toAll, w]);

  // Minimap drag-to-select (brushing moves here)
  const miniDragRef = React.useRef<{ start: number; left: number; width: number } | null>(null);
  const onMiniDown = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const x = clamp(e.clientX - rect.left, 0, rect.width);
    miniDragRef.current = { start: x, left: rect.left, width: rect.width };
    const t = xScaleAll.invert(x);
    setMiniPreview([t, t]);
    const onMove = (ev: MouseEvent) => {
      if (!miniDragRef.current) return;
      const xr = clamp(ev.clientX - miniDragRef.current.left, 0, miniDragRef.current.width);
      const a = xScaleAll.invert(Math.min(miniDragRef.current.start, xr));
      const b = xScaleAll.invert(Math.max(miniDragRef.current.start, xr));
      setMiniPreview([a, b]);
    };
    const onUp = (ev: MouseEvent) => {
      if (!miniDragRef.current) return;
      const xr = clamp(ev.clientX - miniDragRef.current.left, 0, miniDragRef.current.width);
      const dragPx = Math.abs(xr - miniDragRef.current.start);
      if (dragPx >= 2) {
        let nf = xScaleAll.invert(Math.min(miniDragRef.current.start, xr)).getTime();
        let nt = xScaleAll.invert(Math.max(miniDragRef.current.start, xr)).getTime();
        if (nf < fromAll.getTime()) { nf = fromAll.getTime(); }
        if (nt > toAll.getTime()) { nt = toAll.getTime(); }
        applyRange(nf, nt);
      }
      setMiniPreview(null);
      miniDragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('mouseleave', onUp as any);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('mouseleave', onUp as any);
  }, [clamp, xScaleAll, toSel, fromSel, fromAll, toAll, applyRange]);

  React.useEffect(() => {
    setMiniPreview(null);
  }, [fromAll, toAll]);

  // Wheel pan (left/right): translate current selected window within [fromAll, toAll]
  const onWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    const absX = Math.abs(e.deltaX);
    const absY = Math.abs(e.deltaY);
    const hasHorizontal = absX > absY || e.shiftKey;
    if (hasHorizontal) {
      // Horizontal pan
      const dxPx = absX >= absY ? e.deltaX : (e.shiftKey ? e.deltaY : 0);
      if (!dxPx) return;
      // e.preventDefault();
      const centerPx = w / 2;
      const dtMs = (xScaleSel.invert(centerPx + dxPx).getTime() - xScaleSel.invert(centerPx).getTime());
      if (!dtMs) return;
      const span = toSel.getTime() - fromSel.getTime();
      let nf = new Date(fromSel.getTime() + dtMs);
      let nt = new Date(toSel.getTime() + dtMs);
      if (nf < fromAll) { nf = new Date(fromAll); nt = new Date(fromAll.getTime() + span); }
      if (nt > toAll) { nt = new Date(toAll); nf = new Date(toAll.getTime() - span); }
      applyRange(+nf, +nt);
      return;
    }
    // Vertical zoom
    // e.preventDefault();
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    const x = Math.max(0, Math.min(w, e.clientX - rect.left));
    const anchor = xScaleSel.invert(x);
    const anchorMs = anchor.getTime();
    const spanMs = toSel.getTime() - fromSel.getTime();
    const rangeMs = toAll.getTime() - fromAll.getTime();
    const MIN_SPAN_MS = 60 * 1000; // 1 minute minimum
    const factor = Math.pow(1.0018, e.deltaY); // >1 zoom out, <1 zoom in
    let newSpan = Math.max(MIN_SPAN_MS, Math.min(rangeMs, spanMs * factor));
    const ratio = Math.max(0, Math.min(1, (anchorMs - fromSel.getTime()) / spanMs || 0.5));
    let nfMs = anchorMs - ratio * newSpan;
    let ntMs = nfMs + newSpan;
    if (nfMs < fromAll.getTime()) { nfMs = fromAll.getTime(); ntMs = nfMs + newSpan; }
    if (ntMs > toAll.getTime()) { ntMs = toAll.getTime(); nfMs = ntMs - newSpan; }
    applyRange(nfMs, ntMs);
  }, [fromSel, toSel, fromAll, toAll, w, xScaleSel, applyRange]);

  // Two-finger touch: pinch to zoom and drag horizontally to pan (mobile)
  const touchStateRef = React.useRef<{ centerX: number; span: number } | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length !== 2) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const t1 = e.touches[0];
    const t2 = e.touches[1];
    const centerX = ((t1.clientX + t2.clientX) / 2) - rect.left;
    const span = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
    touchStateRef.current = { centerX, span };
    document.documentElement.style['overscrollBehaviorX' as any] = 'none';
    document.documentElement.style['overscrollBehaviorY' as any] = 'none';
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length !== 2 || !touchStateRef.current) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const t1 = e.touches[0];
    const t2 = e.touches[1];
    const nextCenterX = ((t1.clientX + t2.clientX) / 2) - rect.left;
    const nextSpan = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
    const { centerX, span } = touchStateRef.current;

    const dxPx = nextCenterX - centerX;
    const spanDelta = nextSpan - span;

    // Thresholds to avoid jitter
    const PAN_THRESHOLD = 2;
    const ZOOM_THRESHOLD = 2;

    // Horizontal pan when the two-finger center moves significantly
    if (Math.abs(dxPx) > PAN_THRESHOLD) {
      const centerPx = w / 2;
      const dtMs = -(xScaleSel.invert(centerPx + dxPx).getTime() - xScaleSel.invert(centerPx).getTime());
      if (dtMs) {
        const spanMs = toSel.getTime() - fromSel.getTime();
        let nf = new Date(fromSel.getTime() + dtMs);
        let nt = new Date(toSel.getTime() + dtMs);
        if (nf < fromAll) { nf = new Date(fromAll); nt = new Date(fromAll.getTime() + spanMs); }
        if (nt > toAll) { nt = new Date(toAll); nf = new Date(toAll.getTime() - spanMs); }
        setFromSel(nf); setToSel(nt);
      }
    }

    // Pinch zoom when distance between fingers changes
    if (Math.abs(spanDelta) > ZOOM_THRESHOLD && span > 0 && nextSpan > 0) {
      const anchorX = Math.max(0, Math.min(w, nextCenterX));
      const anchor = xScaleSel.invert(anchorX);
      const anchorMs = anchor.getTime();
      const spanMs = toSel.getTime() - fromSel.getTime();
      const rangeMs = toAll.getTime() - fromAll.getTime();
      const factor = span / nextSpan; // pinch in => nextSpan < span => factor > 1 => zoom in
      let newSpan = Math.max(MIN_ZOOM_MS, Math.min(rangeMs, spanMs * factor));
      const ratio = Math.max(0, Math.min(1, (anchorMs - fromSel.getTime()) / spanMs || 0.5));
      let nfMs = anchorMs - ratio * newSpan;
      let ntMs = nfMs + newSpan;
      if (nfMs < fromAll.getTime()) { nfMs = fromAll.getTime(); ntMs = nfMs + newSpan; }
      if (ntMs > toAll.getTime()) { ntMs = toAll.getTime(); nfMs = ntMs - newSpan; }
      applyRange(nfMs, ntMs);
    }

    touchStateRef.current = { centerX: nextCenterX, span: nextSpan };
  }, [w, xScaleSel, fromSel, toSel, fromAll, toAll, applyRange, MIN_ZOOM_MS]);

  const onTouchEnd = useCallback(() => {
    touchStateRef.current = null;
    document.documentElement.style['overscrollBehaviorX' as any] = '';
    document.documentElement.style['overscrollBehaviorY' as any] = '';
  }, []);

  const onMouseOver = useCallback((e: React.MouseEvent) => {
    // document.body.style['overscrollBehaviorX' as any] = 'none';
    document.documentElement.style['overscrollBehaviorX' as any] = 'none';
    document.documentElement.style['overscrollBehaviorY' as any] = 'none';
  }, []);
  const onMouseOut = useCallback((e: React.MouseEvent) => {
    // document.body.style['overscrollBehaviorX' as any] = '';
    document.documentElement.style['overscrollBehaviorX' as any] = '';
    document.documentElement.style['overscrollBehaviorY' as any] = '';
  }, []);
  const overlayCaptureHandlers = React.useMemo(() => ({ onMouseDownCapture: onOverlayDownCapture }), [onOverlayDownCapture]);

  const zoomedTimeRange = React.useMemo(() => [fromSel, toSel, false] as [Date, Date, boolean], [fromSel, toSel]);

  return (
    <Box ref={ref} sx={{ width: '100%', userSelect: 'none' }}>
      {/* Main chart */}
      <Box
        sx={{ position: 'relative', width: '100%', height, mb: 1.5, touchAction: 'none' }}
        onWheel={onWheel}
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        <MiniDepthChart
          timeRange={zoomedTimeRange}
          currentTime={currentTime}
          features={features}
          precip={precip}
          highlightedKey={highlightedKey}
          onClick={onClick}
          height={height}
          loading={loading}
          overlayCaptureHandlers={overlayCaptureHandlers}
          overlayClickSuppressRef={suppressClickRef}
          caption={caption}
          {...props}
        />
      </Box>

      {/* Minimap for brushing */}
        <Box sx={{ position: 'relative', width: '100%', height: hMini + 8, cursor: 'crosshair' }}>
        <Tooltip title="Drag to zoom to a time window" followCursor arrow>
          <svg
            width={w}
            height={hMini}
            role="presentation"
            style={{ display: 'block', overflow: 'visible' }}
            onMouseDown={onMiniDown}
          >
            <rect x={0} y={0} width={w} height={hMini} fill="rgba(0,0,0,0.04)" rx={4} />
            <rect x={0} y={hMini - 4} width={w} height={4} fill="rgba(0,0,0,0.08)" />
            {miniEventBands.map((b, i) => (
              <rect key={i} x={b.x} y={b.y} width={b.width} height={b.height} rx={2} fill={b.color} />
            ))}
            {/* Current selection window (or preview while dragging) */}
            {(() => {
              const a = miniPreview ? miniPreview[0] : fromSel;
              const b = miniPreview ? miniPreview[1] : toSel;
              const x1 = xScaleAll(a);
              const x2 = xScaleAll(b);
              const selX = Math.min(x1, x2);
              const selW = Math.max(4, Math.abs(x2 - x1));
              return (
                <rect
                  x={selX}
                  y={2}
                  width={selW}
                  height={hMini - 6}
                  rx={3}
                  fill={dirty ? "rgba(14,144,137,0.18)" : "rgba(0, 50, 48, 0.186)"}
                  stroke="rgba(14,144,137,0.6)"
                  strokeWidth={1}
                  opacity={miniPreview ? 0.7 : 1}
                />
              );
            })()}
          </svg>
        </Tooltip>
        {dirty && (
          <Box
            sx={{
              position: 'absolute',
              top: '0%',
              left: `${(xScaleAll(fromSel) + xScaleAll(toSel)) / 2}px`,
              transform: 'translate(-50%, 4px)',
              display: 'flex',
              gap: 0.5,
              alignItems: 'center',
              fontSize: 12,
              // color: 'text.secondary',
              userSelect: 'none',
              zIndex: 10,
            }}
          >
            <Tooltip title="Zoom out">
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 1,
                  cursor: 'pointer',
                }}
                onClick={resetSelection}
              >
                ✕
              </Box>
            </Tooltip>
            <Tooltip title="Commit new time range">
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 1,
                  cursor: 'pointer',
                }}
                onClick={commitSelection}
              >
                ✓
              </Box>
            </Tooltip>
          </Box>
        )}
        </Box>
      
    </Box>
  );
}
